// Types for Nutrition Evaluation Feature

// =============================================================================
// Feeding Types
// =============================================================================

export interface FeedingType {
  id: number;
  name: string;
  slug: string;
  template: "regular_meal_template" | "feed_template";
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AppointmentFeedingType {
  id: string;
  appointment: string;
  feeding_type: number;
  feeding_type_details: FeedingType;
}

// =============================================================================
// Nutrition Profile
// =============================================================================

export interface NutritionProfile {
  id?: string;
  appointment: string;
  weight?: number;
  height_ft?: number;
  height_in?: number;
  waist?: number;
  hip?: number;
  bmi?: number;
  bmr?: number;
  pa_calories?: number;
  tdee?: number;
  activity_level?: ActivityLevel;
}

// Health Profile (from /health-profile/ API - used for body measurements, calculated values, and sidebar display)
// This is the main source of truth for weight, height, BMI, TDEE, BMR, PA Calories, etc.
export interface HealthProfile {
  id?: string;
  user_id?: string;
  // Body measurements
  height_in_cms?: number;
  weight_in_kgs?: number;
  waist_in_cms?: number;
  hip_in_cms?: number;
  activity_level?: ActivityLevel;
  city?: string;
  // Calculated values
  bmi?: {
    bmi: number;
    metadata: string;
  };
  bmr?: number;
  tdee?: number;
  pa_calories?: number;
  waist_height_ratio?: {
    waist_height_ratio: number;
    metadata: string;
  };
  waist_hip_ratio?: {
    waist_hip_ratio: number;
    metadata: string;
  } | null;
  ideal_weight?: number;
}

export type HealthProfileUpdateSource =
  | "profile_intake"
  | "doctor_intake"
  | "tracker_app"
  | "tracker_console"
  | "prescription"
  | "meal_plan"
  | "admin"
  | "legacy";
// Activity Level options for Physical Activity dropdown
export type ActivityLevel = "SEDENTARY" | "MODERATE" | "HEAVY" | "";

export const ACTIVITY_LEVEL_OPTIONS = [
  { value: "SEDENTARY" as ActivityLevel, label: "Sedentary" },
  { value: "MODERATE" as ActivityLevel, label: "Moderate" },
  { value: "HEAVY" as ActivityLevel, label: "Heavy" },
] as const;

// Regional Preference options — must match backend REGIONAL_PREFERENCE_CHOICES exactly
export const REGIONAL_PREFERENCE_OPTIONS = [
  { value: "south", label: "South" },
  { value: "punjabi", label: "Punjabi" },
  { value: "bengali", label: "Bengali" },
  { value: "maharashtrian", label: "Maharashtrian" },
  { value: "gujarati", label: "Gujarati" },
  { value: "mughlai", label: "Mughlai" },
  { value: "rajasthani", label: "Rajasthani" },
  { value: "goan", label: "Goan" },
  { value: "hyderabadi_andhra", label: "Hyderabadi / Andhra" },
  { value: "pan_india", label: "Pan India" },
  { value: "continental", label: "Continental" },
  { value: "chinese", label: "Chinese" },
  { value: "sindhi", label: "Sindhi" },
  { value: "kathiawadi", label: "Kathiawadi" },
  { value: "kashmiri", label: "Kashmiri" },
] as const;

// Food Preference (Liked/Disliked Food)
export interface FoodPreferenceItem {
  id: string;
  patient: string;
  food_item: {
    id: number;
    name: string;
    full_name: string;
  };
  is_liked: boolean;
  order: number;
  created_at?: string;
  updated_at?: string;
}

export interface SleepData {
  wake_up_time?: string;
  sleep_time?: string;
  sleep_quality?: "very_poor" | "poor" | "fair" | "good" | "excellent";
  sleep_duration?: string;
  sleep_issues_note?: string;
  has_sleep_issues?: boolean;
}

export interface FoodFrequency {
  fast_food_frequency?: string;
  sweets_frequency?: string;
  packaged_food_frequency?: string;
  bakery_items_frequency?: string;
}

export interface DietaryPreference {
  id?: string;
  dietary_type?: "vegetarian" | "non_vegetarian" | "vegan" | "eggetarian";
  regional_preferences?: string[]; // canonical — array of REGIONAL_PREFERENCE_OPTIONS values
  food_allergies?: string[];
  food_intolerances?: string[];
}

// =============================================================================
// Meal Groups & Diet Recall
// =============================================================================

export type MealTimeCode =
  | "EM"
  | "PB"
  | "BR"
  | "MM"
  | "LU"
  | "EV"
  | "DI"
  | "PW"
  | "PO";

export const MEAL_TIME_LABELS: Record<MealTimeCode, string> = {
  EM: "Early Morning",
  PB: "Pre-Breakfast",
  BR: "Breakfast",
  MM: "Mid-Morning",
  LU: "Lunch",
  EV: "Evening",
  DI: "Dinner",
  PW: "Pre-Workout",
  PO: "Post-Workout",
};

export interface NutrientValue {
  amount: number;
  unit: string;
  rda_percentage: number | null;
  exceeds_tul?: boolean;
  has_tul?: boolean;
}

export interface Nutrient {
  name: string;
  amount: number;
  unit: string;
  percentage: number;
  exceeds_tul: boolean;
  has_tul: boolean;
}

// API returns nutrients as a map: { "Nutrient Name": NutrientValue }
export interface NutrientCategoryMap {
  [nutrientName: string]: NutrientValue;
}

// Legacy interface for backward compatibility
export interface NutrientCategory {
  [key: string]: NutrientValue | NutrientCategoryItem[];
}

export interface NutrientCategoryItem {
  nutrient: {
    amount: number;
    unit: string;
    rda_percentage?: number;
    exceeds_tul?: boolean;
    has_tul?: boolean;
  };
}

// Updated TotalNutrients to match actual API response from /meal-groups/
export interface TotalNutrients {
  general: NutrientCategoryMap;
  carbohydrates: NutrientCategoryMap;
  protein: NutrientCategoryMap;
  lipids: NutrientCategoryMap;
  minerals?: NutrientCategoryMap;
  vitamins?: NutrientCategoryMap;
}

export interface NutrientsSummary {
  energy?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  fiber?: number;
}

export interface FoodItemDetails {
  id: number;
  name: string;
  full_name: string;
  food_code: string;
  energy_kcal: string;
  food_item_nutrient_values?: {
    energy: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  };
}

export interface ServingDetails {
  id: number;
  serving_unit: {
    code: number;
    name: string;
  };
  description: string;
  weight_g: string;
  multiplier: string;
}

export interface MealEntry {
  id: string;
  food_item?: number;
  food_item_details?: FoodItemDetails;
  serving_size?: number;
  serving_details?: ServingDetails;
  quantity: string;
  comment: string;
  is_comment: boolean;
  order: number;
  calculated_energy?: number;
  meal_group: string;
}

export interface MealTime {
  id: string;
  meal_time: MealTimeCode;
  order: number;
  entries: MealEntry[];
  meal_nutrients_summary?: NutrientsSummary;
}

export interface MealData {
  id: string;
  meal_groups: MealTime[];
  total_nutrients: TotalNutrients;
}

// =============================================================================
// Feed Groups (Tube Feeding)
// =============================================================================

export interface FeedEntry {
  id: string;
  // For formula feeds (tube feeding specific)
  feed_item?: number;
  feed_item_details?: {
    id: number;
    name: string;
    brand?: string;
    calories_per_ml?: number;
  };
  volume_ml?: string;
  // For regular food items added to feed groups (matching Flutter's FeedEntry)
  food_item?: number;
  food_item_details?: FoodItemDetails;
  serving_size?: number;
  serving_details?: ServingDetails;
  quantity?: number;
  // Common fields
  comment: string;
  is_comment: boolean;
  order: number;
  calculated_energy?: number;
  feed_group: string;
}

export interface FeedTime {
  id: string;
  label: string; // Feed label (e.g., "Feed 1", "Feed 2")
  time: string; // Time in HH:MM format - matches Flutter API field name
  plan_option?: string; // Optional for meal plan (e.g., "option_1", "option_2")
  order: number;
  entries: FeedEntry[];
  feed_nutrients_summary?: NutrientsSummary;
}

export interface FeedData {
  id: string;
  feed_groups: FeedTime[];
  total_nutrients: TotalNutrients;
}

// =============================================================================
// Set Target
// =============================================================================

export interface MealStrategy {
  id?: string;
  appointment: string;
  strategies: string[];
}

// Meal Strategy History (consolidated view)
export interface MealStrategyHistory {
  id: string;
  appointment: string;
  zarity_appointment_id: string;
  doctor_name: string;
  strategies: string[];
  meal_strategies: string[];
  created_at: string;
}

// Nutrition Target (combined weight + macronutrients - matching Flutter's patient/{appointmentId}/nutrition-targets/)
export interface NutritionTarget {
  id?: string;
  weight?: number; // target weight
  protein?: number;
  carbs?: number;
  fat?: number;
  fibre?: number;
  energy?: number;
  recommended_calories?: number;
  // TDEE and BMR values from calculation (returned from API)
  tdee?: number;
  bmr?: number;
  pa_calories?: number; // Physical activity calories
  energy_requirement?: number;
  // Macronutrient values in grams (returned from API)
  carbs_grams?: number;
  protein_grams?: number;
  fat_grams?: number;
  fiber_grams?: number;
}

export interface WeightEnergyTarget {
  id?: string;
  appointment: string;
  current_weight?: number;
  target_weight?: number;
  weight_difference?: number;
  energy_target?: number;
  energy_requirement?: number;
  protein_per_kg?: number;
  // TDEE and BMR values from calculation
  tdee?: number;
  bmr?: number;
  pa_calories?: number; // Physical activity calories
  carbohydrate_grams?: number;
  protein_grams?: number;
  fat_grams?: number;
}

export interface MacronutrientTarget {
  id?: string;
  appointment: string;
  protein_percentage?: number;
  protein_grams?: number;
  carbs_percentage?: number;
  carbs_grams?: number;
  fat_percentage?: number;
  fat_grams?: number;
  fiber_grams?: number;
  fiber_percentage?: number;
}

// Enhanced macronutrient form data with RDA calculations
export interface MacronutrientFormData {
  // Set Values (grams)
  protein_grams: string;
  carbs_grams: string;
  fat_grams: string;
  fiber_grams: string;
  energy_kcal: string;
  // %E (percentage of energy)
  protein_percentage_e: string;
  carbs_percentage_e: string;
  fat_percentage_e: string;
  fiber_percentage_e: string;
  energy_percentage_e: string; // % of RDA energy
  // RDA ranges
  protein_rda: string;
  carbs_rda: string;
  fat_rda: string;
  fiber_rda: string;
}

// =============================================================================
// Nutrient Conversion Utilities
// =============================================================================

// Conversion constants (kcal per gram)
export const NUTRIENT_KCAL_PER_GRAM = {
  protein: 4,
  carbs: 4,
  fat: 9,
  fiber: 2,
} as const;

// Helper type for calculation functions
export type NutrientCalculations = {
  carbsGramsToKcal: (grams: number) => number;
  carbsKcalToGrams: (kcal: number) => number;
  fatsGramsToKcal: (grams: number) => number;
  fatsKcalToGrams: (kcal: number) => number;
  proteinsGramsToKcal: (grams: number) => number;
  proteinsKcalToGrams: (kcal: number) => number;
  fiberGramsToKcal: (grams: number) => number;
  fiberKcalToGrams: (kcal: number) => number;
  calculateTotalEnergy: (
    protein: number,
    carbs: number,
    fat: number,
    fiber: number
  ) => number;
  calculatePercentageE: (nutrientKcal: number, totalEnergy: number) => number;
  calculateRDA: (
    energyRDA: number,
    weight: number
  ) => {
    protein: string;
    carbs: string;
    fat: string;
    fiber: string;
  };
};

export interface SpecialFeedConsideration {
  id: string;
  consideration: number;
  consideration_details?: {
    id: number;
    consideration: string;
  };
}

export interface SpecialFeedConsiderationOption {
  id: number;
  consideration: string;
}

// =============================================================================
// Meal Plan
// =============================================================================

export interface MealPlan {
  id: string;
  name: string;
  index: number;
  is_active: boolean;
  created_at: string;
}

export interface MealPlanData extends MealData {
  plan_index: number;
}

// =============================================================================
// Symptoms for Nutrition
// =============================================================================

export interface NutritionSymptom {
  id: number;
  name: string;
  category?: string;
  type?: "digestive" | "others"; // Flutter API field for symptom type
}

export interface AppointmentNutritionSymptom {
  id: string;
  appointment: string;
  symptom?: number;
  symptoms?: NutritionSymptom; // Flutter API uses 'symptoms' instead of 'symptom_details'
  symptom_details?: NutritionSymptom;
  notes?: string;
  status?: string;
  // Aligned with the backend draft-item contract. Inactive draft items are
  // hidden from the UI; on publish the activity_status is propagated to the
  // committed PatientSymptoms row.
  activity_status?: "active" | "inactive";
  is_active?: boolean;
}

// =============================================================================
// API Response Types
// =============================================================================

export interface NutritionEvaluationResponse {
  id: string;
  appointment: string;
  feeding_type?: AppointmentFeedingType;
  nutrition_profile?: NutritionProfile;
  diet_recall?: MealData;
  meal_plans?: MealPlanData[];
  targets?: {
    weight_energy: WeightEnergyTarget;
    macronutrients: MacronutrientTarget;
    meal_strategy: MealStrategy;
  };
}

// =============================================================================
// Component Props Types
// =============================================================================

export interface NutritionEvaluationProps {
  appointmentId: string;
  patientId: string;
  isCompleted?: boolean;
}

export interface NutritionTabProps {
  appointmentId: string;
  isCompleted?: boolean;
}

// =============================================================================
// Utility Types for Forms
// =============================================================================

export interface NutritionProfileFormData {
  weight: string;
  height_ft: string;
  height_in: string;
  waist: string;
  hip: string;
}

export interface AddFoodItemFormData {
  meal_time: MealTimeCode;
  food_item: number;
  serving_size: number;
  quantity: string;
}

export interface AddFeedFormData {
  time: string; // Matches Flutter API field name
  feed_item: number;
  volume_ml: string;
}

export interface AddNoteFormData {
  meal_time: MealTimeCode;
  comment: string;
}
